import { useCallback, useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import { ocClientState } from "oc/states/client-state";

type Primitive = string | boolean | number;
type StoredValue = Primitive | object;

/**
 * Hook kliens oldali állapot kezelésére Recoil atomFamily-vel.
 * Backward-compatible API a régi Apollo cache alapú implementációval.
 *
 * @param key - Egyedi azonosító az állapothoz
 * @param initialValue - Kezdőérték
 * @returns [value, setValue] tuple
 */
export function useOcClientState<T extends StoredValue>(
  key: string,
  initialValue: T,
): [T, (value: T) => void] {
  const [value, setValue] = useRecoilState(ocClientState(key));
  const initializedRef = useRef(false);
  const type = typeof initialValue;

  // Inicializálás az első renderkor, ha nincs még érték
  useEffect(() => {
    if (!initializedRef.current && value === undefined) {
      setValue(initialValue);
      initializedRef.current = true;
    }
  }, [initialValue, setValue, value]);

  // Merge funkció object típusnál a backward compatibility érdekében
  const setValueWithMerge = useCallback(
    (newValue: T) => {
      if (type === "object" && newValue !== null && !(newValue instanceof Date)) {
        setValue((prev: unknown) => {
          if (prev === undefined || prev === null) {
            return newValue;
          }
          return {
            ...(prev as object),
            ...(newValue as object),
          };
        });
      } else {
        setValue(newValue);
      }
    },
    [setValue, type],
  );

  // Ha nincs még érték (undefined), használjuk az initialValue-t
  const resultValue = value === undefined ? initialValue : (value as T);

  return [resultValue, setValueWithMerge];
}

export default useOcClientState;
